import { Close } from "@mui/icons-material";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContent } from "../../../../../shared/components";
import { ToolbarGridFilterProposal } from "../../../../../shared/components/toolbarGridProposal/ToolbarGridFilterProposal";
import { useToast } from "../../../../../shared/hooks";
import { ComissionServices, ComissionStatusData } from "../../../../../shared/services/api/comission/ComissionServices";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../../../shared/guards/ProfileGuard";

// Importações dos Modais
import { ModalFinancialLaunch } from "./ModalFinancialLaunch";
import { ModalReactivateComission } from "./ModalReactivateComission";
import { ModalSelectInstallmentsToReactivate } from "./ModalSelectInstallmentsToRevert";
import { ComissionCard } from "../components/ComissionCard";
import { ModalInstallmentComissionDetails } from "./ModalIntallmentComissionDetail";
import { ModalSelectComissionsToCancel } from "./ModalSelectComissionsToCancel";
import { ModalCancelComissions } from "./ModalCancelComissions";
import { ModalSelectComissionToReactivate } from "./ModalSelectComissionToReactivate";
import { ModalComissionLaunch } from "./ModalComissionLaunch";

// --- NOVOS TIPOS (Mantidos) ---
interface IComission {
	id: string;
	number?: string;
	dueDate: string | null;
	status: string;
	type: string;
	value: string;
	proposalId: string;
	description: string;
	// 💡 Adicionado para o filtro de parcelas
	installmentId: string | null;
	proposal: { certificateNumber: number };
	createdAt?: string | null;
}

interface ITotalStatusComission {
	total: number;
	ativo: number;
	cancelada: number;
}

interface ModalInstallmentsComissionProps {
	isOpen: boolean;
	onClose: () => void;
	rows: any;
	isSuperAdmin: boolean;
}

// ... (calculateTotalsAndFilter function, mantida inalterada) ...

const calculateTotalsAndFilter = (comissions: IComission[], filter: string): { filtered: IComission[]; totals: ITotalStatusComission } => {
	let ativoCount = 0;
	let canceladaCount = 0;

	const filtered = comissions.filter((comission) => {
		const normalizedStatus = comission.status.toLowerCase();
		const isActive = normalizedStatus !== "canceled" && normalizedStatus !== "cancelado";

		if (normalizedStatus === "canceled" || normalizedStatus === "cancelado") {
			canceladaCount++;
		}

		if (isActive) {
			ativoCount++;
		}

		if (filter === "Todas") {
			return true;
		} else if (filter === "active") {
			return isActive;
		} else if (filter === "canceled") {
			return normalizedStatus === "canceled" || normalizedStatus === "cancelado";
		}
		return false;
	});

	return {
		filtered: filtered,
		totals: {
			total: comissions.length,
			ativo: ativoCount,
			cancelada: canceladaCount,
		},
	};
};

export const ModalInstallmentsComission = ({ isOpen, onClose, rows, isSuperAdmin }: ModalInstallmentsComissionProps) => {
	const [statusFilter, setStatusFilter] = useState("Todas");
	const [rawComissions, setRawComissions] = useState<IComission[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [sortBy, setSortBy] = useState<"parcel" | "dueDate">("dueDate");

	// Estados de Modal/Ação
	const [openCancelModal, setOpenCancelModal] = useState(false);
	const [cancelRow, setCancelRow] = useState<IComission | null>(null);
	const [openSelectBatch, setOpenSelectBatch] = useState(false);
	const [rowsToCancel, setRowsToCancel] = useState<IComission[]>([]);
	const [openSelectBatchReactivate, setOpenSelectBatchReactivate] = useState(false);
	const [rowsToReactivate, setRowsToReactivate] = useState<IComission[]>([]);
	const [openModalReactivate, setOpenModalReactivate] = useState(false);
	const [selectedRowDetails, setSelectedRowDetails] = useState<IComission | null>(null);
	const [openModalFinancial, setOpenModalFinancial] = useState(false);

	const toast = useToast();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const canLaunchComissionIndividual = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.CommissionLaunchSingle);
	const canCancelComissionBatch = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.CommissionCancelBatch);
	const canReactivateComissionBatch = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.CommissionReactivateBatch);
	const canCancelSingle = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.CommissionCancelSingle);
	const canViewDetails = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.CommissionViewDetails);

	// --- CÁLCULO DE TOTAIS E FILTROS DINÂMICOS (useMemo) ---
	const { filteredComissions, totals } = useMemo(() => {
		const mappedComissions: IComission[] = rawComissions.map((item: any) => ({
			...item,
			id: item.id,
			number: item.number,
			dueDate: item.dueDate ?? null,
			status: item.status,
			type: item.type,
			value: item.value,
			proposalId: item.proposalId,
			description: item.description,
			// 💡 Garante que installmentId está sendo mapeado
			installmentId: item.installmentId ?? null,
			createdAt: item.createdAt ?? null,
			proposal: item.proposal ?? { certificateNumber: 0 },
		}));

		const results = calculateTotalsAndFilter(mappedComissions, statusFilter);
		return {
			filteredComissions: results.filtered,
			totals: results.totals,
		};
	}, [rawComissions, statusFilter]);

	// ✅ ATUALIZADO: Geração da lista de IDs de parcelas JÁ UTILIZADAS para bônus (ignora canceladas)
	const usedInstallmentIds = useMemo(() => {
		return rawComissions
			.filter((comission) => {
				const isBonus = comission.type.toLowerCase() === "bonus";
				const hasInstallmentId = comission.installmentId;
				// 💡 Exclui comissões CANCELADAS da lista de IDs BLOQUEADOS
				const isNotCanceled = comission.status.toLowerCase() !== "canceled" && comission.status.toLowerCase() !== "cancelado";

				return isBonus && hasInstallmentId && isNotCanceled;
			})
			.map((comission) => comission.installmentId!);
	}, [rawComissions]);

	// --- FUNÇÃO DE BUSCA (ESTÁVEL) ---
	const fetchComissions = useCallback(() => {
		if (!rows?.id) return;

		setIsLoading(true);
		ComissionServices.getComissionByProposalID(rows.id)
			.then((response) => {
				setIsLoading(false);

				if (response instanceof Error) {
					toast.present({ type: "error", messageOrContent: <ToastContent title="Erro" content={response.message} /> });
					return;
				}

				// 💡 Adapta a resposta para garantir que tem o installmentId, se vier do backend
				setRawComissions(
					(response.data.map((item) => ({
						...item,
						// Garante a tipagem correta para o novo campo installmentId
						installmentId: (item as any).installmentId || null,
					})) as IComission[]) || []
				);
			})
			.catch((err) => {
				setIsLoading(false);
				const { titleMessage, contentMessage } = returnMessageError(err);
				toast.present({ type: "error", messageOrContent: <ToastContent title={titleMessage} content={contentMessage} /> });
			});
	}, [rows?.id]);

	useEffect(() => {
		if (isOpen) {
			fetchComissions();
		}
	}, [isOpen, fetchComissions]); // As dependências são estáveis

	// --- HANDLERS ---
	const handleCancel = (row: IComission) => {
		setCancelRow(row);
		setOpenCancelModal(true);
	};

	const handleView = (row: IComission) => {
		if (!canViewDetails) return;
		setSelectedRowDetails(row);
	};

	return (
		<>
			<Dialog open={isOpen} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						pb: 1,
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<Typography fontWeight={400} variant="h6" color="primary.main">
						Extrato de Comissõess
					</Typography>
					<IconButton onClick={onClose}>
						<Close />
					</IconButton>
				</DialogTitle>

				<Box
					sx={{
						px: 6,
						py: 1,
						position: "sticky",
						top: 0,
						zIndex: 2,
						backgroundColor: theme.palette.background.paper,
						borderBottom: `1px solid ${theme.palette.divider}`,
					}}
				>
					<ToolbarGridFilterProposal
						onClickAll={() => setStatusFilter("Todas")}
						onClickActive={() => setStatusFilter("active")}
						onClickCanceled={() => setStatusFilter("canceled")}
						selectAll={statusFilter === "Todas"}
						selectAtive={statusFilter === "active"}
						selectCanceled={statusFilter === "canceled"}
						textButtonAll={`Todas (${totals.total ?? 0})`}
						textButtonActive={`Ativas/Pagas (${totals.ativo ?? 0})`}
						textButtonCanceled={`Canceladas (${totals.cancelada ?? 0})`}
						onCancelBatch={canCancelComissionBatch ? () => setOpenSelectBatch(true) : undefined}
						onReactivateBatch={canReactivateComissionBatch ? () => setOpenSelectBatchReactivate(true) : undefined}
						onLaunchValues={canLaunchComissionIndividual ? () => setOpenModalFinancial(true) : undefined}
						sortBy={sortBy}
						onChangeSort={(value) => setSortBy(value)}
					/>
				</Box>

				<DialogContent dividers sx={{ maxHeight: "80vh", px: 6, py: 2 }}>
					<Stack spacing={2}>
						{isLoading && <LinearProgress />}
						{!isLoading &&
							[...filteredComissions]
								.sort((a, b) => {
									if (sortBy === "parcel") {
										const numA = Number(a.number) || 0;
										const numB = Number(b.number) || 0;
										return numA - numB;
									}
									const dateA = new Date(a.dueDate || a.createdAt || 0).getTime();
									const dateB = new Date(b.dueDate || b.createdAt || 0).getTime();
									return dateA - dateB;
								})
								.map((item: any) => {
									const comissionRowData = {
										...item,
										createdAt: item.createdAt ?? null,
										dueDate: item.dueDate ?? null,
										comissionId: item.comissionId ?? item.id,
										userId: item.userId ?? null,
										installmentValue: item.installmentValue ?? item.value,
										updatedAt: item.updatedAt ?? null,
										paymentDate: item.paymentDate ?? null,
										paymentMethod: item.paymentMethod ?? null,
										paymentStatus: item.paymentStatus ?? null,
										canceledAt: item.canceledAt ?? null,
										canceledBy: item.canceledBy ?? null,
										canceledReason: item.canceledReason ?? null,
									};
									return (
										<ComissionCard
											key={item.id}
											row={comissionRowData as any}
											canViewDetails={canViewDetails}
											canCancel={canCancelSingle}
											onCancel={() => handleCancel(item as IComission)}
											onView={handleView}
										/>
									);
								})}
					</Stack>
				</DialogContent>
			</Dialog>

			{/* Modal cancelamento (CONFIRMAÇÃO) */}
			{openCancelModal && (
						<ModalCancelComissions
					isOpenModal={openCancelModal}
					isClose={() => {
						setOpenCancelModal(false);
						setCancelRow(null);
						setRowsToCancel([]);
					}}
					isSuperAdmin={isSuperAdmin}
					row={cancelRow as any}
					rows={rowsToCancel.length > 0 ? (rowsToCancel as any) : undefined}
					onSuccess={() => fetchComissions()}
				/>
			)}

			{selectedRowDetails && canViewDetails && (
				<ModalInstallmentComissionDetails
					open={Boolean(selectedRowDetails)}
					onClose={() => setSelectedRowDetails(null)}
					row={selectedRowDetails as any}
				/>
			)}

			{/* Seleção em lote para CANCELAR (SELETOR) */}
			{openSelectBatch && (
				<ModalSelectComissionsToCancel
					installments={rawComissions.filter((i) => i.status.toLowerCase() === "preview" || i.status.toLowerCase() === "pending") as any}
					onClose={() => setOpenSelectBatch(false)}
					onConfirm={(selectedIds) => {
						const selectedRows = rawComissions.filter((r) => selectedIds.includes(r.id));
						setRowsToCancel(selectedRows as IComission[]);
						setOpenSelectBatch(false);
						setOpenCancelModal(true);
					}}
				/>
			)}

			{/* Seleção em lote para REATIVAR (SELETOR) */}
			{openSelectBatchReactivate && (
				<ModalSelectComissionToReactivate
					installments={rawComissions.filter((i) => i.status.toLowerCase() === "canceled" || i.status.toLowerCase() === "cancelado") as any}
					onClose={() => setOpenSelectBatchReactivate(false)}
					onConfirm={(selectedIds) => {
						const selectedRows = rawComissions.filter((r) => selectedIds.includes(r.id));
						setRowsToReactivate(selectedRows as IComission[]);
						setOpenSelectBatchReactivate(false);
						setOpenModalReactivate(true);
					}}
				/>
			)}

			{/* Modal de confirmação de REATIVAÇÃO (Componente atualizado) */}
			{openModalReactivate && (
				<ModalReactivateComission // <-- Substituição pela nova versão (Comission)
					isOpenModal={openModalReactivate}
					isClose={() => {
						setOpenModalReactivate(false);
						setRowsToReactivate([]);
					}}
					isSuperAdmin={isSuperAdmin}
					rows={rowsToReactivate as any}
					onSuccess={() => fetchComissions()}
				/>
			)}

			{openModalFinancial && canLaunchComissionIndividual && (
				<ModalComissionLaunch
					isOpenModal={openModalFinancial}
					isClose={() => setOpenModalFinancial(false)}
					isUpdate={fetchComissions}
					rows={rows}
					// ✅ Aqui passamos a lista filtrada, permitindo o reuso de parcelas canceladas
					usedInstallmentIds={usedInstallmentIds}
				/>
			)}
		</>
	);
};
